import { useRecoilValue } from 'recoil';

import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

import { LogEntry } from 'common/graphqlTypes';
import { DataNode } from 'common/types';
import { getFacetsAsTree } from 'common/util/facets';
import { quote } from 'common/util/string';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import FacetFilter from 'components/common/FacetFilter/FacetFilter';
import { LogDetailsProps } from 'components/common/LogDetails/LogDetails';
import Tree from 'components/common/Tree/Tree';
import TruncateWithTooltip from 'components/common/TruncateWithTooltip/TruncateWithTooltip';
import 'components/common/LogDetails/LogDetails.scss';
import { pluginState } from 'components/common/Managers/ViewStateManager.state';
import { Schema } from 'schemas';

import './LogDetailsFacetsTree.scss';

const MAX_KEY_LENGTH = 25;

type LogDetailsFacetsTreeProps = {
  logEntry: LogEntry;
  fieldFilter: string;
  isExpanded?: boolean;
  onClose: LogDetailsProps['onClose'];
  schema?: Schema;
};

const LogDetailsFacetsTree = ({
  logEntry,
  fieldFilter,
  isExpanded = false,
  onClose,
  schema,
}: LogDetailsFacetsTreeProps) => {
  const copy = useCopyToClipboard();
  const plugin = useRecoilValue(pluginState);

  const transformKey = (node: DataNode) => {
    const key = node.key as string;
    return key.length < MAX_KEY_LENGTH ? (
      node.key
    ) : (
      <TruncateWithTooltip text={key} maxLength={MAX_KEY_LENGTH} id={key} />
    );
  };

  const transformValue = (node: DataNode) => {
    const key = node.fullKey as string;
    const value = (node.value ?? '') as string;
    const fieldType = schema?.fields[key]?.type;
    const FieldRenderer = fieldType && plugin?.fieldRenderers?.[fieldType];

    return (
      <>
        {FieldRenderer ? (
          <FieldRenderer fieldId={key} value={value} row={logEntry?.values} />
        ) : (
          <span className="LogDetailsFacetsTree__Value">{String(node.value)}</span>
        )}
        <span className="LogDetailsFacetsTree__Filter">
          <FacetFilter
            field={key}
            value={isString(value) ? quote(value, false, true) : value}
            valueIsNumeric={isNumber(value)}
            onSearch={onClose}
            onCopy={() => copy(String(value), 'value')}
          />
        </span>
      </>
    );
  };

  const schemaFields = Object.keys(schema?.fields || {}); // only show fields that are part of the schema (DPVZ-2267)
  const tree = getFacetsAsTree(logEntry, schemaFields, fieldFilter, transformKey, transformValue);

  return (
    <div className="LogDetails__FieldsContainer LogDetailsFacetsTree">
      <Tree
        root={tree}
        className="LogDetailsFacetsTree__Tree"
        itemClassName="LogDetailsFacetsTree__TreeRow"
        keyClassName="LogDetailsFacetsTree__TreeKey"
        valueClassName="LogDetailsFacetsTree__TreeValue"
        isExpanded={isExpanded}
      />
    </div>
  );
};

export default LogDetailsFacetsTree;
